import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateCustomerInput,
  Customer,
  CustomerListScope,
  CustomerMemberOption,
  CustomerTypeOption,
  PaginatedResult,
  SortDirection,
  UpdateCustomerInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface CustomerListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  typeId?: string;
  typeIds?: string[];
  scope?: CustomerListScope;
  sortBy?: string;
  sortDir?: SortDirection;
}

const customerKeys = {
  all: ['customers'] as const,
  list: (params: CustomerListParams) => ['customers', 'list', params] as const,
  detail: (id: string) => ['customers', 'detail', id] as const,
};

/** Customer-type options for the customer form (readable by any customers user). */
export function useCustomerFormTypeOptions(): UseQueryResult<CustomerTypeOption[]> {
  return useQuery({
    queryKey: ['customers', 'type-options'],
    queryFn: () => api.get<CustomerTypeOption[]>('/customers/type-options'),
  });
}

/** Member options (with role flags) for the customer form's primary-member pickers. */
export function useCustomerFormMemberOptions(): UseQueryResult<CustomerMemberOption[]> {
  return useQuery({
    queryKey: ['customers', 'member-options'],
    queryFn: () => api.get<CustomerMemberOption[]>('/customers/member-options'),
  });
}

export function useCustomers(
  params: CustomerListParams,
): UseQueryResult<PaginatedResult<Customer>> {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Customer>>(
        `/customers${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isActive: params.isActive,
          typeId: params.typeId,
          typeIds:
            params.typeIds && params.typeIds.length > 0 ? params.typeIds.join(',') : undefined,
          scope: params.scope,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCustomer(id: string | undefined): UseQueryResult<Customer> {
  return useQuery({
    queryKey: customerKeys.detail(id ?? ''),
    queryFn: () => api.get<Customer>(`/customers/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useCreateCustomer(): UseMutationResult<Customer, unknown, CreateCustomerInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) => api.post<Customer>('/customers', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export interface UpdateCustomerArgs {
  id: string;
  input: UpdateCustomerInput;
}

export function useUpdateCustomer(): UseMutationResult<Customer, unknown, UpdateCustomerArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateCustomerArgs) =>
      api.patch<Customer>(`/customers/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useDeleteCustomer(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}
